import React, { useState, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import LoginCard from '../components/LoginCard';
import LoginLayout from '../components/LoginLayout';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { ReactComponent as PhoneCall } from '../../../assets/icons/svg/colored-icons/ic-colored-phone-call.svg';
import { ReactComponent as TextMessage } from '../../../assets/icons/svg/colored-icons/ic-colored-sms.svg';

import { RESEND_VERIFICATION_SCREEN_DATA } from '../constants';
import { useStore } from '../../../store';
import { useVerifyMutation as verifyMutation } from '../../../hooks/mutations/useVerifyMutation';

import { ILoginRadioButtons, IServerError } from '../../../types';
import { LOGIN_NAVIGATION_MAP } from '../components/LoginNavigation/constants';
import { SERVER_ERRORS_DATA } from '../../../constants/serverErrors';
import { LoginHelpOption } from './LoginHelp/LoginHelpOption';
import { FORGOT_PASSWORD_FLOW } from './LoginHelp/constants';

const ResendVerification = () => {
  const [currentButton, setCurrentButton] = useState<ILoginRadioButtons | undefined>();
  const [radioButtons, setRadioButtons] = useState(new Map());

  const setCurrentPage = useStore((state) => state.setCurrentPage);
  const user = useStore((state) => state.user);

  const setUserMfaType = () => {
    user.mfaType = currentButton?.TYPE ? currentButton.TYPE : 'SMS';
  };

  const { LOGIN_CARD, HEADING, TEXT, RADIO_BUTTONS, LOGIN_BTN, ACTIONS } = RESEND_VERIFICATION_SCREEN_DATA;

  const { mutate: mutateVerify, isLoading } = verifyMutation(
    true,
    () => {
      setUserMfaType();
      setCurrentPage(ACTIONS.SUCCESS);
    },
    (errorObj: IServerError) => {
      if (
        errorObj?.response?.data?.errors?.[0]?.code === SERVER_ERRORS_DATA.MFA_TOO_MANY_RESEND_REQUEST_CODE ||
        errorObj?.response?.data?.errors?.[0]?.codeName === SERVER_ERRORS_DATA.MFA_TOO_MANY_RESEND_REQUEST_CODE_NAME
      ) {
        setCurrentPage(LOGIN_NAVIGATION_MAP.TAKE_5);
      }
    },
  );

  useEffect(() => {
    const newCurrentButton = RADIO_BUTTONS.find((radioButton) => 'CHECKED' in radioButton && !!radioButton.CHECKED);
    const buttons = new Map();

    RADIO_BUTTONS.forEach((radioButton) => {
      buttons.set(radioButton.ID, radioButton);
    });

    setCurrentButton(newCurrentButton);
    setRadioButtons(buttons);
  }, []);

  const handleButtonClick = (id: string) => {
    setCurrentButton(radioButtons.get(id));
  };

  const handleOnClick = () => {
    mutateVerify({
      customerId: user?.customerId,
      customData: { ...user.loginResponseCustomData },
      mfaType: currentButton?.TYPE || user.mfaType,
      rememberMe: false,
    });
  };

  const { t } = useTranslation();

  return (
    <LoginLayout backButton backDestination={ACTIONS.BACK}>
      <LoginCard className={LOGIN_CARD.CLASS_NAME}>
        <Heading title={t(HEADING.TITLE)} className={HEADING.CLASS_NAME} />
        <Heading title={t(TEXT.TITLE)} className={TEXT.CLASS_NAME} />
        <div className='flex flex-col'>
          <LoginHelpOption
            shouldHideArrow
            isSelected={currentButton?.ID === RADIO_BUTTONS[0].ID}
            title={t(FORGOT_PASSWORD_FLOW.MFA_METHOD.SMS_METHOD)}
            icon={<TextMessage />}
            onClick={() => handleButtonClick(RADIO_BUTTONS[0].ID)}
            testId='mfa_method_sms'
          />
          <LoginHelpOption
            shouldHideArrow
            isSelected={currentButton?.ID === RADIO_BUTTONS[1].ID}
            title={t(FORGOT_PASSWORD_FLOW.MFA_METHOD.CALL_METHOD)}
            icon={<PhoneCall />}
            onClick={() => handleButtonClick(RADIO_BUTTONS[1].ID)}
            testId='mfa_method_voice'
          />
        </div>
        <Button
          isLoading={isLoading}
          disable={isLoading}
          id='resendVerification'
          onClick={handleOnClick}
          title={t(currentButton?.BUTTON_TITLE ?? '', { defaultValue: '' })}
          className={LOGIN_BTN.CLASS_NAME}
        />
      </LoginCard>
    </LoginLayout>
  );
};

export default ResendVerification;
